import { useState } from "react";
import { deletePart } from "@/lib/api";
import { Pencil, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fmtMoney = (n) => (n === null || n === undefined || n === "" ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n));

function statusTone(s) {
  switch (s) {
    case "Needed": return "bg-amber-500/10 text-amber-400 border-amber-500/20";
    case "Ordered": return "bg-sky-500/10 text-sky-400 border-sky-500/20";
    case "Shipped": return "bg-violet-500/10 text-violet-400 border-violet-500/20";
    case "Received": return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
    case "Backorder": return "bg-rose-500/10 text-rose-400 border-rose-500/20";
    case "Cancelled": return "bg-neutral-500/10 text-[#3A2A10] border-neutral-500/20";
    default: return "bg-[#2B2214]/[0.14] text-[#3A2A10] border-[#8B6A1F]/45";
  }
}

export default function PartsTable({ parts, onEdit, onReload }) {
  const [confirmDel, setConfirmDel] = useState(null);

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await deletePart(confirmDel.id);
      toast.success(`Removed ${confirmDel.part_name}`);
      setConfirmDel(null);
      onReload();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (parts.length === 0) {
    return (
      <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] py-16 text-center" data-testid="parts-empty">
        <div className="font-serif-display text-2xl text-[#0A0604] mb-2">No parts tracked</div>
        <div className="text-xs text-[#3A2A10] uppercase tracking-[0.2em]">Add a mainspring, balance staff, or jewel to begin</div>
      </div>
    );
  }

  return (
    <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] overflow-x-auto" data-testid="parts-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#8B6A1F]/35">
            {["Part","Type","Supplier","Status","Ordered","Received","Qty","Cost/ea","Total","Tracking","Actions"].map((h) => (
              <th key={h} className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal whitespace-nowrap">{h === "Actions" ? "" : h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parts.map((p) => (
            <tr key={p.id} className="border-b border-[#8B6A1F]/30 hover:bg-[#2B2214]/[0.06] group" data-testid={`part-row-${p.id}`}>
              <td className="px-3 py-3 text-[#0A0604]">{p.part_name}</td>
              <td className="px-3 py-3 text-[#3A2A10]">{p.part_type || "—"}</td>
              <td className="px-3 py-3 text-[#3A2A10]">{p.supplier || "—"}</td>
              <td className="px-3 py-3">
                <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm ${statusTone(p.order_status)}`}>
                  {p.order_status || "—"}
                </span>
              </td>
              <td className="px-3 py-3 font-mono-data text-[#0A0604] whitespace-nowrap">{p.order_date || "—"}</td>
              <td className="px-3 py-3 font-mono-data text-[#0A0604] whitespace-nowrap">{p.received_date || "—"}</td>
              <td className="px-3 py-3 font-mono-data text-[#0A0604] text-right">{p.quantity ?? "—"}</td>
              <td className="px-3 py-3 font-mono-data text-[#0A0604] text-right">{fmtMoney(p.cost_each)}</td>
              <td className="px-3 py-3 font-mono-data text-[#3A2A10] text-right">{fmtMoney(p.total_cost)}</td>
              <td className="px-3 py-3 font-mono-data text-[#3A2A10] text-xs">{p.tracking_number || "—"}</td>
              <td className="px-3 py-3 whitespace-nowrap text-right">
                <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => onEdit(p)} className="p-1.5 text-[#3A2A10] hover:text-[#3A2A10]" data-testid={`part-edit-${p.id}`} aria-label="Edit"><Pencil size={13} /></button>
                  <button onClick={() => setConfirmDel(p)} className="p-1.5 text-[#3A2A10] hover:text-rose-400" data-testid={`part-delete-${p.id}`} aria-label="Delete"><Trash2 size={13} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Remove part?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">This will permanently delete <span className="text-[#0A0604]">{confirmDel?.part_name}</span>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#8B6A1F]/45 rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm" data-testid="part-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
